import {
  Users, Mail, Hash, Phone, MessageCircle, Edit2, Check, X,
  Zap, Crown, ShoppingBag, UserPlus, FileText, Package, ChevronLeft
} from "lucide-react";
import { Button } from "../ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/avatar";
import { getUserTagType, TAG_CONFIG, isUserLive, getRelativeTime, openWhatsApp, openEmail, WHATSAPP_MESSAGE } from "./adminHelpers";

const TAG_ICONS = { vip: Crown, purchased: ShoppingBag, new: UserPlus };

export const MobileUserCardList = ({
  users, editingUser, editForm, setEditForm, isSaving,
  onStartEdit, onSaveEdit, onCancelEdit,
  onToggleTrialKit, onMarkWhatsAppContacted,
  onSelectUser,
}) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-16 bg-white dark:bg-[#1a231a] rounded-sm border border-[#2C3329]/10">
        <Users className="h-10 w-10 text-[#A3A3A3] mx-auto mb-3" />
        <p className="font-sans text-sm text-[#5C5C5C]">לא נמצאו משתמשים</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-mobile-cards" className="space-y-3">
      {users.map((u) => (
        <MobileUserCard
          key={u.user_id}
          u={u}
          editingUser={editingUser}
          editForm={editForm}
          setEditForm={setEditForm}
          isSaving={isSaving}
          onStartEdit={onStartEdit}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
          onToggleTrialKit={onToggleTrialKit}
          onMarkWhatsAppContacted={onMarkWhatsAppContacted}
          onSelectUser={onSelectUser}
        />
      ))}
    </div>
  );
};

const MobileUserCard = ({ u, editingUser, editForm, setEditForm, isSaving, onStartEdit, onSaveEdit, onCancelEdit, onToggleTrialKit, onMarkWhatsAppContacted, onSelectUser }) => {
  const tagType = getUserTagType(u);
  const tag = TAG_CONFIG[tagType];
  const TagIcon = TAG_ICONS[tagType];
  const live = isUserLive(u.last_login);
  const isEditing = editingUser === u.user_id;

  return (
    <div data-testid={`mobile-card-${u.user_id}`} className="bg-white dark:bg-[#1a231a] border border-[#2C3329]/10 dark:border-[#2a3a2a] rounded-sm p-4">
      {/* Header */}
      <button onClick={() => onSelectUser(u)} className="flex items-center gap-3 w-full text-right mb-3">
        <div className="relative">
          <Avatar className="h-11 w-11 border border-[#2C3329]/10">
            <AvatarImage src={u.picture} alt={u.name} />
            <AvatarFallback className="bg-[#4A5D43]/10 text-[#4A5D43] font-sans">{u.name?.charAt(0) || '?'}</AvatarFallback>
          </Avatar>
          {live && <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#6B8E6B] rounded-full border-2 border-white dark:border-[#1a231a]" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-sans text-sm font-medium text-[#2C3329] dark:text-white truncate">{u.name || 'ללא שם'}</p>
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${tag.bg} ${tag.text}`}>
              {TagIcon && <TagIcon className="h-2.5 w-2.5" />}{tag.label}
            </span>
          </div>
          <p className="font-sans text-xs text-[#A3A3A3] truncate">{u.email}</p>
        </div>
        <ChevronLeft className="h-4 w-4 text-[#A3A3A3] shrink-0" />
      </button>

      {/* Data Grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <DataCell label="ערכה">
          <button
            data-testid={`mobile-toggle-kit-${u.user_id}`}
            onClick={() => onToggleTrialKit(u.user_id, !(u.received_trial_kit))}
            className={`w-8 h-4 rounded-full relative transition-colors ${u.received_trial_kit ? 'bg-[#4A5D43]' : 'bg-[#2C3329]/15'}`}
          >
            <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${u.received_trial_kit ? 'right-0.5' : 'right-[14px]'}`} />
          </button>
        </DataCell>
        <DataCell label="PDF">
          {u.pdf_download_count > 0 ? (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-[#7B8C96]/10 text-[#7B8C96]">
              <FileText className="h-2.5 w-2.5" />{u.pdf_download_count}x
            </span>
          ) : <span className="text-xs text-[#A3A3A3]">—</span>}
        </DataCell>
        <DataCell label="קופון">
          {u.coupon_code ? (
            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${u.coupon_used ? 'bg-[#6B8E6B]/10 text-[#6B8E6B]' : 'bg-[#C67D63]/10 text-[#C67D63]'}`}>
              <Hash className="h-2.5 w-2.5" />{u.coupon_code}
            </span>
          ) : <span className="text-xs text-[#A3A3A3]">—</span>}
        </DataCell>
      </div>

      {/* Phone + Activity */}
      <div className="flex items-center justify-between text-xs border-t border-[#2C3329]/5 dark:border-[#2a3a2a] pt-3">
        <div className="flex items-center gap-1.5 text-[#5C5C5C]">
          <Phone className="h-3 w-3" />
          {isEditing ? (
            <input
              data-testid={`mobile-edit-phone-${u.user_id}`}
              type="tel"
              value={editForm.phone}
              onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
              className="w-24 px-2 py-1 text-xs rounded-sm border border-[#4A5D43]/40 bg-white dark:bg-[#121812]"
              placeholder="050-0000000"
              dir="ltr"
            />
          ) : (
            <span dir="ltr">{u.phone || 'אין טלפון'}</span>
          )}
        </div>
        {live ? (
          <span className="text-[#6B8E6B] font-medium flex items-center gap-1">
            <Zap className="h-3 w-3" />באתר
          </span>
        ) : (
          <span className="text-[#A3A3A3]">{getRelativeTime(u.last_login)}</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#2C3329]/5 dark:border-[#2a3a2a]">
        {isEditing ? (
          <>
            <Button data-testid={`mobile-save-${u.user_id}`} size="sm" variant="ghost" onClick={() => onSaveEdit(u.user_id)} disabled={isSaving} className="flex-1 h-8 text-[#6B8E6B] hover:bg-[#6B8E6B]/10 rounded-sm text-xs">
              <Check className="h-3.5 w-3.5 me-1" />{isSaving ? 'שומר...' : 'שמור'}
            </Button>
            <Button data-testid={`mobile-cancel-${u.user_id}`} size="sm" variant="ghost" onClick={onCancelEdit} className="flex-1 h-8 text-[#C67D63] hover:bg-[#C67D63]/10 rounded-sm text-xs">
              <X className="h-3.5 w-3.5 me-1" />ביטול
            </Button>
          </>
        ) : (
          <>
            {u.phone && (
              <Button data-testid={`mobile-whatsapp-${u.user_id}`} size="sm" variant="ghost" onClick={() => { openWhatsApp(u.phone, WHATSAPP_MESSAGE); onMarkWhatsAppContacted(u.user_id); }} className="flex-1 h-8 text-[#6B8E6B] hover:bg-[#6B8E6B]/10 rounded-sm text-xs">
                <MessageCircle className="h-3.5 w-3.5 me-1" />WhatsApp
              </Button>
            )}
            <Button data-testid={`mobile-email-${u.user_id}`} size="sm" variant="ghost" onClick={() => openEmail(u.email, u.name)} className="flex-1 h-8 text-[#7B8C96] hover:bg-[#7B8C96]/10 rounded-sm text-xs">
              <Mail className="h-3.5 w-3.5 me-1" />אימייל
            </Button>
            <Button data-testid={`mobile-edit-${u.user_id}`} size="sm" variant="ghost" onClick={() => onStartEdit(u)} className="h-8 w-8 p-0 text-[#5C5C5C] hover:bg-[#2C3329]/5 rounded-sm">
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const DataCell = ({ label, children }) => (
  <div className="flex flex-col items-center p-2 bg-[#FAF8F5] dark:bg-[#121812] rounded-sm">
    <span className="font-sans text-[10px] tracking-[0.03em] text-[#A3A3A3] mb-1">{label}</span>
    {children}
  </div>
);
